// App-wide success / failure messages.
// Every add, save, delete or action (POST / PUT / PATCH / DELETE) shows a popup when it finishes: green on success,
// red with the reason on failure. Pages that already show their own message are left alone, so nothing appears twice.
// To silence one call: api.post(url, body, { silent: true }).
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";

// --- remember when a page showed its own message ----------------------------------------------------------
let lastPageToast = 0;
if (!toast.__feedbackPatched) {
  ["success", "error", "warning", "info", "message", "promise"].forEach((k) => {
    const orig = toast[k];
    if (typeof orig !== "function") return;
    toast[k] = (...args) => {
      const id = args[1] && typeof args[1] === "object" ? String(args[1].id || "") : "";
      if (!id.startsWith("fb-")) lastPageToast = Date.now();
      return orig.apply(toast, args);
    };
  });
  toast.__feedbackPatched = true;
}

// Calls that are not "saving something" even though they use POST: lookups, previews, logins, read-marks, exports.
const SKIP = /(\/auth\/|\/login|\/token|notifications|\/search|\/preview|\/suggest|\/lookup|\/calculate|\/validate|\/extract|\/export|\/print|\/pdf|\/download|\/check|\/autofill|\/refresh|\/heartbeat|\/read(\b|-all)|\/mark-read)/i;

const ACTION = {
  submit: "submitted", approve: "approved", reject: "rejected", post: "posted", cancel: "cancelled", confirm: "confirmed",
  accept: "accepted", receive: "received", close: "closed", reopen: "reopened", certify: "certified", release: "released",
  allocate: "allocated", revalue: "revalued", dispose: "disposed", capitalise: "capitalised", scan: "scan completed",
  "offer-stock": "offered", "seed-defaults": "added",
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function words(seg) {
  return seg.replace(/[-_]/g, " ");
}
function singular(w) {
  if (/ies$/.test(w)) return w.replace(/ies$/, "y");
  if (/(ss|us)$/.test(w)) return w;
  if (/ses$/.test(w)) return w.slice(0, -2);
  return w.replace(/s$/, "");
}
function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "Vendor added", "Purchase order approved", "Changes saved", "Deleted" - worked out from the URL. */
export function successText(method, url = "") {
  const path = url.split("?")[0].split("/").filter(Boolean).filter((s) => !UUID.test(s) && !/^\d+$/.test(s));
  const last = path[path.length - 1] || "";
  const prev = path[path.length - 2] || "";
  if (ACTION[last]) {
    const noun = prev && !["projects", "procurement", "masters", "inventory", "revenue"].includes(prev) ? singular(words(prev)) : "";
    return cap(`${noun ? noun + " " : ""}${ACTION[last]}`.trim());
  }
  const noun = singular(words(last));
  if (method === "delete") return `${cap(noun || "Item")} deleted`;
  if (method === "post") return `${cap(noun || "Record")} added`;
  return `${cap(noun || "Changes")} ${noun ? "updated" : "saved"}`;
}

const MUTATING = new Set(["post", "put", "patch", "delete"]);
const recent = [];

function tooMany() {
  const now = Date.now();
  while (recent.length && now - recent[0] > 2000) recent.shift();
  if (recent.length >= 3) return true;
  recent.push(now);
  return false;
}

export function installFeedback(api) {
  api.interceptors.response.use(
    (response) => {
      const cfg = response.config || {};
      const method = (cfg.method || "").toLowerCase();
      if (MUTATING.has(method) && !cfg.silent && !SKIP.test(cfg.url || "")) {
        const finished = Date.now();
        const serverMsg = typeof response.data?.message === "string" ? response.data.message : null;
        setTimeout(() => {
          if (lastPageToast >= finished - 50 || tooMany()) return;   // the page already told the user
          toast.success(serverMsg || successText(method, cfg.url), { id: `fb-ok-${method}-${cfg.url}`, duration: 3500 });
        }, 500);
      }
      return response;
    },
    (error) => {
      const cfg = error?.config || {};
      const method = (cfg.method || "").toLowerCase();
      const status = error?.response?.status;
      const mutation = MUTATING.has(method);
      const serverProblem = !error?.response || status >= 500;
      const skip = cfg.silent || status === 401;
      if (!skip && (mutation || serverProblem)) {
        const finished = Date.now();
        const text = error?.response ? getErrorMessage(error) : "Cannot reach the server. Check your connection and try again.";
        setTimeout(() => {
          if (lastPageToast >= finished - 50) return;
          toast.error(text, { id: `fb-err-${method}-${cfg.url}`, duration: 8000 });
        }, 500);
      }
      return Promise.reject(error);
    }
  );
}
